import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AuthLayout, authFormStyles as s } from '@/components/auth/AuthLayout'
import { authApi } from '@/api/authApi'

export default function ForgotPasswordPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [email, setEmail] = useState(searchParams.get('email') ?? '')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      await authApi.requestPasswordReset(email)
      // The API answers the same way for an unknown address (it must not reveal who
      // has an account), so the next screen is always the code form.
      navigate(`/reset-password?email=${encodeURIComponent(email)}`, { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AuthLayout
      title={t('auth.forgotPassword.title', 'Recuperar senha')}
      subtitle={t(
        'auth.forgotPassword.subtitle',
        'Informe seu e-mail e enviamos um código para você criar uma nova senha.',
      )}
      footer={
        <span>
          {t('auth.forgotPassword.remembered', 'Lembrou da senha?')}{' '}
          <Link to="/login" style={{ color: 'var(--accent)' }}>
            {t('auth.forgotPassword.login', 'Entrar')}
          </Link>
        </span>
      }
    >
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {error ? <div style={s.error}>{error}</div> : null}
        <div style={s.field}>
          <label style={s.label} htmlFor="email">
            {t('auth.fields.email', 'E-mail')}
          </label>
          <input
            id="email"
            type="email"
            required
            autoFocus
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            style={s.input}
          />
        </div>
        <button type="submit" style={s.button} disabled={submitting}>
          {submitting
            ? t('auth.forgotPassword.submitting', 'Enviando...')
            : t('auth.forgotPassword.submit', 'Enviar código')}
        </button>
      </form>
    </AuthLayout>
  )
}
